import { useHandler } from "@redotech/react-util/hook";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import {
  ActionStepType,
  actionStepTypes,
  actionStepTypeToFriendlyName,
  SkipCondition,
  TriggerStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { Category } from "@redotech/redo-model/advanced-flow/triggers";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { ConjunctionMode } from "@redotech/redo-model/segments/segment-types";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { LabelText } from "@redotech/redo-web/arbiter-components/common/descriptors";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { blockBorderProps } from "@redotech/redo-web/marketing/segments/segment-builder-editor";
import { Text } from "@redotech/redo-web/text";
import { lowercaseFirstLetter } from "@redotech/string/transform";
import { ObjectId } from "bson";
import { memo, useMemo, useState } from "react";
import { getConditionSummary } from "../../nodes/branch-node";
import {
  DeepPartial,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../../steps";
import { ConditionExpressionInspector, expressionIsValid } from "../condition";

type ConditionExpressionType = NonNullable<
  TriggerStep["skipConditions"]
>["conditions"][number];

export const SkipConditionsInspector = memo(function SkipConditionsInspector({
  step,
  setStep,
  schema,
  category,
}: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>) {
  const addNewCondition = useHandler(() => {
    setStep({
      ...step,
      skipConditions: {
        conjunctionMode: ConjunctionMode.OR,
        ...step.skipConditions,
        conditions: [
          ...(step.skipConditions?.conditions ?? []),
          {
            dataSource: undefined,
            actionTypes: actionStepTypes,
            _id: new ObjectId().toString(),
          },
        ],
      },
    });
  });

  const removeCondition = useHandler((index: number) => {
    const newConditions = step.skipConditions?.conditions ?? [];
    newConditions.splice(index, 1);
    setStep({
      ...step,
      skipConditions: { ...step.skipConditions, conditions: newConditions },
    });
  });

  return (
    <Flex dir="column" gap="xl">
      <Flex align="center" justify="space-between">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Skip conditions
        </Text>
        <RedoButton
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={addNewCondition}
          size="xs"
          text="Skip condition"
        />
      </Flex>
      <Text fontSize="xxs" textColor="secondary">
        Skip action steps when any condition is met
      </Text>
      {step.skipConditions?.conditions?.map((condition, index) => {
        return (
          <SkipConditionBlock
            category={category}
            condition={condition}
            key={condition._id}
            removeCondition={() => removeCondition(index)}
            schema={schema}
            setCondition={(newCondition) => {
              const newConditions = step.skipConditions?.conditions ?? [];
              newConditions.splice(index, 1, newCondition);
              setStep({
                ...step,
                skipConditions: {
                  ...step.skipConditions,
                  conditions: newConditions,
                },
              });
            }}
          />
        );
      })}
    </Flex>
  );
});

const SkipConditionBlock = memo(function SkipConditionBlock({
  condition,
  setCondition,
  removeCondition,
  schema,
  category,
}: {
  condition: DeepPartial<SkipCondition>;
  setCondition: (condition: DeepPartial<SkipCondition>) => void;
  removeCondition: () => void;
  schema: Schema;
  category: Category;
}) {
  const isValid = useMemo(() => expressionIsValid(condition), [condition]);
  const [collapsed, setCollapsed] = useState(isValid);

  const actionTypeSummary = useMemo(() => {
    if (
      !condition.actionTypes ||
      actionStepTypes.every((type) => condition.actionTypes?.includes(type))
    ) {
      return "all actions";
    }
    return condition.actionTypes
      .map((type) => lowercaseFirstLetter(actionStepTypeToFriendlyName[type]))
      .join(", ");
  }, [condition.actionTypes]);

  const conditionSummary = useMemo(() => {
    return getConditionSummary(condition);
  }, [condition]);

  const actionTypeOptions: BadgeRedoListItem<ActionStepType>[] = useMemo(() => {
    return actionStepTypes.map((type) => ({
      value: type,
      id: type,
      type: "badge",
      badge: { text: actionStepTypeToFriendlyName[type] },
    }));
  }, []);

  const blockContents = collapsed ? (
    <Flex dir="column" gap="sm">
      <Text fontSize="xxs" textColor="tertiary">
        Skip condition
      </Text>
      <Text fontSize="xs">
        Skips {actionTypeSummary} when {lowercaseFirstLetter(conditionSummary)}
      </Text>
    </Flex>
  ) : (
    <>
      <LabelText label="Action type" size="xs" />
      <RedoTagsDropdownInput<ActionStepType>
        options={actionTypeOptions}
        placeholder="Select action types"
        selectedOptions={actionTypeOptions.filter((o) =>
          condition?.actionTypes?.includes(o.value),
        )}
        setSelectedOptions={(options) => {
          setCondition({
            ...condition,
            actionTypes: options.map((o) => o.value),
          });
        }}
        size="xs"
      />
      <ConditionExpressionInspector
        category={category}
        expression={condition}
        schema={schema}
        setExpression={(newExpression) => {
          setCondition(
            {
              _id: condition._id,
              actionTypes: condition.actionTypes,
              ...newExpression,
            } as ConditionExpressionType, // We get some kind of conflict here without a cast. I think it's to do with the recursive type in the array match expression
          );
        }}
        showTitle={false}
      />
      <Divider />
    </>
  );

  return (
    <Flex
      {...blockBorderProps}
      // Shouldn't be a possible state in the current implementation, but just in case
      {...(!isValid && collapsed ? { borderColor: "error" } : {})}
      dir="column"
      gap="xl"
      key={condition._id}
      p="xl"
    >
      {blockContents}
      <Flex align="center">
        <RedoButton
          disabled={!collapsed && !isValid}
          hierarchy="secondary"
          onClick={() => setCollapsed((c) => !c)}
          size="xs"
          text={collapsed ? "Edit" : "Save"}
        />
        <RedoButton
          hierarchy="tertiary"
          IconLeading={TrashIcon}
          onClick={removeCondition}
          size="xs"
        />
      </Flex>
    </Flex>
  );
});
